// Read-only custom field cells for the Projects list's hand-built table. Each cell is a
// TdLink to the row, like every other cell on this list; values truncate to one line with the full value in
// the design-system hover box only while cut off (TruncatedValue), and a Multi-select value shows the whole names
// that fit, then "+N". Cells are read-only, so there is no editability tooltip.
import React from 'react';
import Styled from 'styled-components';
import TdLink from '../../../../components/td_link';
import {
	CUSTOM_FIELD_COLUMN_WIDTH_PX,
	customFieldColumnId,
	formatCustomFieldValue,
	type BooleanLabels,
	type CustomFieldColumnDef,
} from '../../../shared/components/custom-field-columns/customFieldColumnModel';
import {HeaderName, TruncatedValue} from '../../../shared/components/custom-field-columns/CustomFieldText';
import {projectCustomFieldDateParts} from './projectCustomFieldColumns';
import {CustomFieldMultiSelectText} from '../../../../components/CustomFieldMultiSelectText';

export interface CustomFieldFormat {
	locale: string;
	decimalSeparator?: ',' | '.';
	// The translated Yes / No a Yes/No column shows.
	booleanLabels: BooleanLabels;
}

const WIDTH = `${CUSTOM_FIELD_COLUMN_WIDTH_PX}px`;
const cellCy = (definition: CustomFieldColumnDef): string => `projects-col-cf-${definition.key}-cell`;

// Label plus the native sort-indicator spacer side by side, as the native sortable headers render them.
const HeaderContent = Styled.div`
	display: inline-flex;
	align-items: flex-start;
	max-width: 100%;
`;

export const ProjectCustomFieldHeaderCell = ({
	definition,
	sortBy,
	onSort,
}: {
	definition: CustomFieldColumnDef;
	sortBy: {column: string | null; ascending: boolean};
	onSort: (columnId: string) => void;
}): JSX.Element => {
	const columnId = customFieldColumnId(definition);
	const direction = sortBy.column === columnId ? (sortBy.ascending ? 'ascending' : 'descending') : '';
	return (
		<th
			aria-label={definition.displayName}
			data-cy={`projects-col-cf-${definition.key}-header`}
			className={`sortable ${direction}`}
			onClick={() => onSort(columnId)}
			style={{minWidth: WIDTH, width: WIDTH, maxWidth: WIDTH}}
		>
			<HeaderContent>
				<HeaderName name={definition.displayName} />
				{direction ? <span data-cy="cf-header-sort-indicator">&nbsp;&nbsp;</span> : null}
			</HeaderContent>
		</th>
	);
};

export const ProjectCustomFieldCell = ({
	definition,
	raw,
	path,
	format,
}: {
	definition: CustomFieldColumnDef;
	raw: string | null | undefined;
	path: string;
	format: CustomFieldFormat;
}): JSX.Element => {
	let content: React.ReactNode = null;
	if (definition.kind === 'DATE') {
		const parts = projectCustomFieldDateParts(raw);
		content = parts ? (
			<span>
				<span className="month-day">{parts.monthDay}</span>
				<span className="year">{parts.year}</span>
			</span>
		) : null;
	} else if (definition.kind === 'MULTI_SELECT') {
		// The whole names that fit, then "+N", with every name in the hover box. The cell is already a link to the
		// project, so the names take no tab stop of their own.
		content = (
			<CustomFieldMultiSelectText
				value={raw}
				options={definition.options}
				label={definition.displayName}
				focusable={false}
			/>
		);
	} else {
		// A Yes/No value always has text (not set reads No). A Hyperlink is plain text: the cell is already a
		// link to the project, and the project page shows the field as a link.
		const display = formatCustomFieldValue(definition, raw, format.locale, format.decimalSeparator, format.booleanLabels);
		content = display ? <TruncatedValue value={display} /> : null;
	}
	return <TdLink path={path} dataCy={cellCy(definition)} width={WIDTH} content={content} />;
};

// Connected-project groups have no value of their own: a blank link to the group, like the group row's
// other empty cells.
export const ProjectCustomFieldGroupCell = ({
	definition,
	path,
	style,
}: {
	definition: CustomFieldColumnDef;
	path: string;
	style?: React.CSSProperties;
}): JSX.Element => <TdLink path={path} dataCy={cellCy(definition)} width={WIDTH} style={style} content={''} />;
